import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/pages/LandingPage.css";
import "../styles/pages/MobileResponsiveOverrides.css"; // Added mobile responsive overrides
import {
  isUserVerified,
} from "../utils/sessionUtils";
// Regular imports for immediate loading (no lazy loading to prevent spinners)
import Section2 from "../components/home/Section2";
import BillboardSection from "../components/home/BillboardSection";
import Section3 from "../components/home/Section3";

// Image paths - using direct relative paths from the public folder
const BG_IMAGE_PATH = "/images/section1/hero-bg.png";
const LOGO_IMAGE_PATH = "/images/LOGO.png";
// Optimized carousel images for better caching
const CAROUSEL_IMAGE_PATHS = [
  `/Landingpage.jpg`,
  `/Landingpage-1.jpg`,
  `/Landingpage-2.jpg`,
  `/Landingpage-3.jpg`,
];
const SECTION2_BG_IMAGE_PATH = "/images/section2/video-bg.png";
const SECTION3_MOBILE_BG_IMAGE = "/images/section3/section3bgwithoutimage.jpg";
const SECTION3_DESKTOP_IMAGE = "/images/section3/model-copy.png";
const GROUP_30A_IMAGE = "/images/section1/Group 30a (1).png";
const PROFILE_IMAGES = [
  "/Mask group.png",
  "/Mask group-1.png",
  "/Mask group-2.png",
  "/Mask group-3.png",
  "/Mask group-4.png",
  "/Mask group-5.png",
  "/Mask group-6.png",
  "/Mask group (42).png",
];

// Preloading removed for faster initial page load

const LandingPage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [profileImageIndex, setProfileImageIndex] = useState(0);
  const [loanTextIndex, setLoanTextIndex] = useState(0);
  const [userVerified, setUserVerified] = useState(false);

  // Check if user is already verified
  useEffect(() => {
    setUserVerified(isUserVerified());
  }, []);

  // Loan text carousel content
  const loanTextContent = [
    {
      topLine: "Get loan up to",
      bottomLine: "₹1 Cr*",
    },
    {
      topLine: "Enjoy interest rates starting at",
      bottomLine: "15%* p.a.",
    },
    {
      topLine: "Repay easily over",
      bottomLine: "60 months*",
    },
    {
      topLine: "Track loan status",
      bottomLine: "In Real Time",
    },
    {
      topLine: "Collateral-Free",
      bottomLine: "Loans*",
    },
  ];

  // Preloading removed - images load naturally for faster initial page load

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();

    // Throttle resize event for better performance
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkIfMobile, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Carousel navigation handlers
  const nextSlide = React.useCallback(() => {
    setActiveIndex((prev) =>
      prev === CAROUSEL_IMAGE_PATHS.length - 1 ? 0 : prev + 1
    );
  }, []);

  // Profile image carousel - automatic rotation (optimized for both mobile and desktop)
  useEffect(() => {
    // Slower rotation on both mobile and desktop to reduce CPU usage and improve LCP
    const rotationInterval = isMobile ? 15000 : 8000; // Increased from 6000/3000 to 15000/8000
    
    const interval = setInterval(() => {
      setProfileImageIndex((prev) =>
        prev === PROFILE_IMAGES.length - 1 ? 0 : prev + 1
      );
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [isMobile]);

  // Loan text carousel - automatic rotation (mobile-optimized)
  useEffect(() => {
    // Slower rotation on mobile to reduce CPU usage, desktop stays same for engagement
    const textRotationInterval = isMobile ? 12000 : 4000; // Increased mobile from 8000 to 12000

    const interval = setInterval(() => {
      setLoanTextIndex((prev) =>
        prev === loanTextContent.length - 1 ? 0 : prev + 1
      );
    }, textRotationInterval);

    return () => clearInterval(interval);
  }, [isMobile, loanTextContent.length]);

  // Auto-advance carousel (mobile-optimized)
  useEffect(() => {
    // Slower carousel on mobile to reduce CPU usage, desktop stays same for engagement
    const carouselInterval = isMobile ? 10000 : 4000; // Increased mobile from 8000 to 10000

    const interval = setInterval(() => {
      nextSlide();
    }, carouselInterval);

    return () => clearInterval(interval);
  }, [activeIndex, nextSlide, isMobile]);

  // Create hero section style with background
  const heroStyle = {
    backgroundImage: `url(${BG_IMAGE_PATH})`,
  };

  // Create section2 background style
  const section2Style = {
    backgroundImage: `url(${SECTION2_BG_IMAGE_PATH})`,
  };

  // Create section3 background style
  const section3Style = {
    backgroundImage: `url(${BG_IMAGE_PATH})`,
  };

  // Create carousel image styles
  const cardStyles = CAROUSEL_IMAGE_PATHS.map((path) => ({
    backgroundImage: `url(${path})`,
  }));

  const goToSlide = React.useCallback((index) => {
    setActiveIndex(index);
  }, []);



  // Removed loading state - page renders immediately

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <Link to="/">
            <img
              src={LOGO_IMAGE_PATH}
              alt="Business Loan Game Changer"
              className="hero-logo"
            />
          </Link>{" "}
          <div className="hero-text-container">
            {" "}
            {/* Profile image carousel - left side */}{" "}
            <div className="profile-image-container">
              <img
                src={PROFILE_IMAGES[profileImageIndex]}
                alt="Profile"
                className="profile-carousel-image"
                fetchPriority="high"
                decoding="sync"
                loading="eager"
              />
            </div>{" "}
            <div className="hero-subtitle"> Get Your </div>{" "}
            <div className="neo-highlight poster-text line-1">
              Free Personalised{" "}
            </div>{" "}
            <div className="neo-highlight poster-text line-2">
              Business Poster{" "}
            </div>{" "}
            {userVerified ? (
              <Link to="/add-details" className="btn-neo poster-btn">
                <strong> Continue My Poster </strong> <span>&gt;</span>
              </Link>
            ) : (
              <Link to="/otp-verification" className="btn-neo poster-btn">
                <strong> Make My Poster Now </strong> <span>&gt;</span>
              </Link>
            )}{" "}
          </div>{" "}
          {!isMobile ? (
            <div className="testimonial-container">
              {" "}
              {/* First three testimonial cards */}{" "}
              {cardStyles.slice(0, 3).map((style, index) => (
                <div key={index} className="testimonial-card" style={style}>
                  <div className="neo-extension"> </div>{" "}
                </div>
              ))}{" "}
              {/* Fourth card with Group 30a image attached to it */}{" "}
              <div className="fourth-card-container">
                {" "}
                {/* Group 30a image positioned relative to the 4th card */}{" "}
                <img
                  src={GROUP_30A_IMAGE}
                  alt="Group 30a"
                  className="group-30a-image"
                  loading="lazy"
                />{" "}
                {/* The fourth testimonial card */}{" "}
                <div className="testimonial-card" style={cardStyles[3]}>
                  <div className="neo-extension"> </div>{" "}
                </div>{" "}
              </div>{" "}
            </div>
          ) : (
            <>
              {" "}
              {/* Mobile layout with profile image */}{" "}
              <div className="mobile-frame-container">
                <div className="profile-image-container">
                  <img
                    src={PROFILE_IMAGES[profileImageIndex]}
                    alt="Profile"
                    className={`profile-carousel-image ${
                      profileImageIndex === 0
                        ? "mask-group-base"
                        : profileImageIndex === 4
                        ? "mask-group-4"
                        : profileImageIndex === 5
                        ? "mask-group-5"
                        : ""
                    }`}
                    fetchPriority="high"
                    decoding="sync"
                    loading="eager"
                  />{" "}
                </div>

                <img
                  src={GROUP_30A_IMAGE}
                  alt="Game Changer"
                  className="group-30a-image"
                  loading="lazy"
                />
              </div>{" "}
              {/* Auto-scrolling carousel with one card per view */}{" "}
              <div className="testimonial-container">
                {" "}
                {/* Show only the active card */}{" "}
                <div
                  className="testimonial-card active"
                  style={{
                    backgroundImage: `url(${CAROUSEL_IMAGE_PATHS[activeIndex]})`,
                  }}
                >
                  <div className="neo-extension"> </div>{" "}
                </div>{" "}
              </div>{" "}
              {/* Separate container for dots below the testimonial card */}{" "}
              <div className="carousel-dots-container">
                {" "}
                {CAROUSEL_IMAGE_PATHS.map((_, index) => (
                  <div
                    key={index}
                    className={`carousel-dot ${
                      index === activeIndex ? "active" : ""
                    }`}
                    onClick={() => goToSlide(index)}
                  />
                ))}{" "}
              </div>{" "}
            </>
          )}{" "}
        </div>{" "}
      </section>{" "}
      {/* All sections load immediately without loading spinners */}{" "}
      <Section2 style={section2Style} />{" "}
      <BillboardSection userVerified={userVerified} />{" "}
      <Section3
        style={{
          ...section3Style,
          backgroundImage: `url(${
            isMobile
              ? SECTION3_MOBILE_BG_IMAGE
              : "/images/section3/section3bg.jpg"
          })`,
        }}
        logoImagePath={LOGO_IMAGE_PATH}
        desktopImagePath={SECTION3_DESKTOP_IMAGE}
        loanTextContent={loanTextContent}
        loanTextIndex={loanTextIndex}
        isMobile={isMobile}
      />{" "}
    </div>
  );
};

export default LandingPage;
